/** Pure view-model helpers for the room. No React, fully unit-tested. */
import {
  formatClock,
  team,
  type CallLock,
  type CallOffer,
  type Choice,
  type ProbabilityPoint,
  type Resolution,
  type RoomSnapshot,
  type Side,
} from '@clutch/core';

export const pct = (p: number): number => Math.round(p * 100);
export const isProb = (p: unknown): p is number => typeof p === 'number' && Number.isFinite(p) && p >= 0 && p <= 1;

/** P(subject side wins) from a model point. */
export function pSide(point: ProbabilityPoint, side: Side): number {
  return side === 'home' ? point.pHome : 1 - point.pHome;
}

export type ChanceView =
  | { status: 'loading' }
  | { status: 'ready'; side: Side; pct: number; delta?: number; modelVersion: string; atSeq: number };

/**
 * Chance bar for the call subject (or the home team before a call exists).
 * Only real model points from the server feed this; with none, it's "model loading".
 */
export function chanceView(s: Pick<RoomSnapshot, 'probability' | 'call'>): ChanceView {
  const pts = s.probability;
  const last = pts[pts.length - 1];
  if (!last || !isProb(last.pHome)) return { status: 'loading' };
  const side: Side = s.call?.subject ?? 'home';
  const now = pct(pSide(last, side));
  const prev = pts[pts.length - 2];
  const delta = prev && isProb(prev.pHome) ? now - pct(pSide(prev, side)) : undefined;
  return {
    status: 'ready',
    side,
    pct: now,
    delta: delta === 0 ? undefined : delta,
    modelVersion: last.modelVersion,
    atSeq: last.seq,
  };
}

export const choiceLabel = (call: Pick<CallOffer, 'yesLabel' | 'noLabel'>, c: Choice): string =>
  c === 'yes' ? call.yesLabel : call.noLabel;

/** "LOCKED · Q4 2:11 · 24% at lock" (chance omitted when the model didn't price it). */
export function lockStamp(lock: Pick<CallLock, 'lockedAt' | 'pChoiceAtLock'>): string {
  const parts = ['LOCKED', formatClock(lock.lockedAt.period, lock.lockedAt.clockSec)];
  if (isProb(lock.pChoiceAtLock)) parts.push(`${pct(lock.pChoiceAtLock)}% at lock`);
  return parts.join(' · ');
}

export function cutoffLabel(call: Pick<CallOffer, 'cutoff'>): string {
  return formatClock(call.cutoff.period, call.cutoff.clockSec);
}

export function rejectionCopy(reason: CallLock['reason'], call: Pick<CallOffer, 'cutoff'>): string {
  switch (reason) {
    case 'after_cutoff':
      return `Too late — calls closed at ${cutoffLabel(call)}`;
    case 'already_locked':
      return 'You already locked this one.';
    case 'not_in_room':
      return 'Join the room first, then call it.';
    case 'unknown_call':
      return 'That call is no longer open.';
    default:
      return 'Clutch could not lock that call.';
  }
}

/** "Malik called Celtics" chips — everyone except me, in lock order. */
export function friendCalls(s: Pick<RoomSnapshot, 'locks' | 'call'>, myFanId: string | undefined): string[] {
  const call = s.call;
  if (!call) return [];
  return s.locks
    .filter((l) => l.callId === call.callId && l.status === 'confirmed' && l.fanId !== myFanId)
    .map((l) => `${firstName(l.displayName)} called ${choiceLabel(call, l.choice)}`);
}

/** First name; a "(bot)" label on seeded participants is always kept (honesty rule). */
export function firstName(displayName: string): string {
  const bot = /\s*\(bot\)\s*$/i.test(displayName);
  const first = displayName.replace(/\s*\(bot\)\s*$/i, '').trim().split(/\s+/)[0] ?? displayName;
  return bot ? `${first} (bot)` : first;
}

export function initials(displayName: string): string {
  const parts = displayName
    .replace(/\([^)]*\)/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const a = parts[0]?.[0] ?? '?';
  const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
  return (a + b).toUpperCase();
}

export type ResolutionCopy = {
  tone: 'correct' | 'incorrect' | 'spectator' | 'void';
  headline: string;
  sub?: string;
};

/** The buzzer moment, for this fan. Wrong calls are never shamed (B5). */
export function resolutionCopy(
  res: Resolution,
  myFanId: string | undefined,
  myLock: Pick<CallLock, 'pChoiceAtLock'> | undefined,
): ResolutionCopy {
  if (res.outcome === 'void') return { tone: 'void', headline: 'No result', sub: 'This call was voided. Nobody loses it.' };
  const mine = res.results.find((r) => r.fanId === myFanId);
  const winners = res.results.filter((r) => r.correct && r.fanId !== myFanId).map((r) => firstName(r.displayName));
  if (!mine) {
    return {
      tone: 'spectator',
      headline: 'FINAL',
      sub: winners.length ? `${listNames(winners)} called it.` : 'You watched it happen.',
    };
  }
  if (mine.correct) {
    const p = myLock?.pChoiceAtLock;
    return {
      tone: 'correct',
      headline: 'YOU CALLED IT',
      sub: isProb(p) && p < 0.5 ? `You beat a ${pct(p)}% shot.` : undefined,
    };
  }
  return {
    tone: 'incorrect',
    headline: 'Bold call.',
    sub: winners.length ? `${listNames(winners)} ${winners.length === 1 ? 'gets' : 'get'} this one.` : 'Next one’s yours.',
  };
}

function listNames(names: string[]): string {
  if (names.length <= 2) return names.join(' & ');
  return `${names.slice(0, 2).join(', ')} & ${names.length - 2} more`;
}

/** "Thunder" from a side, via game meta tricodes. */
export function sideName(s: Pick<RoomSnapshot, 'meta'>, side: Side): string {
  return team(side === 'home' ? s.meta.home.tricode : s.meta.away.tricode).name;
}
export function sideTricode(s: Pick<RoomSnapshot, 'meta'>, side: Side): string {
  return side === 'home' ? s.meta.home.tricode : s.meta.away.tricode;
}

/** Which side does a choice back? yes → subject, no → the other side. */
export function choiceSide(call: Pick<CallOffer, 'subject'>, c: Choice): Side {
  if (c === 'yes') return call.subject;
  return call.subject === 'home' ? 'away' : 'home';
}

/** The fan-facing replay honesty badge. */
export function replayBadge(speed: number): string {
  const s = Number.isInteger(speed) ? String(speed) : speed.toFixed(1);
  return `REPLAY · real play-by-play · ${s}×`;
}

// ───────────── Onboarding ─────────────

export const BUDGET_OPTIONS = [
  { value: 1, label: 'Rarely', hint: '1 / week' },
  { value: 3, label: 'Sometimes', hint: '3 / week' },
  { value: 7, label: 'Big moments', hint: '7 / week' },
] as const;

/** "Got it, Jay — Knicks, Brunson, about 3 big moments a week." */
export function readBack(input: { displayName: string; team?: string; player?: string; budgetPerWeek: number }): string {
  const name = firstName(input.displayName);
  const bits = [input.team ? team(input.team).name : undefined, input.player?.trim() || undefined].filter(
    (x): x is string => !!x,
  );
  const n = input.budgetPerWeek;
  const freq = `about ${n} big moment${n === 1 ? '' : 's'} a week`;
  return `Got it, ${name} — ${[...bits, freq].join(', ')}.`;
}
